import React from 'react';
import ErrorBoundary from 'react-error-boundary';
import ErrorFallback from '../ErrorFallback';
import ActivateFailed from './ActivateFailed';
import ActivateSuccess from './ActivateSuccess';
import ActivateSuccessRuntime from './ActivateSuccessRuntime';
import ActivatePublish from './ActivatePublish';
import { Button, Modal } from 'react-bootstrap';
import PlayerSource from '../../sources/player';
import '../../../css/flow/activate.less';
import { fetchFlowLibraryItems } from '../../actions/reduxActions/flowLibrary';
import { connect } from 'react-redux';
import { pathOr } from 'ramda';
import { addNotification } from '../../actions/reduxActions/notification';
import { RUNTIME_URI, SYSTEM_ROLES } from '../../constants';
import ActivateRuntime from './ActivateRuntime';
import { getLocalRuntimesForTenant } from '../../actions/reduxActions/localRuntimes';
import { notify } from '../../utils/errors';

class Activate extends React.Component {

    constructor(props) {
        super(props);

        this.onActivate = this.onActivate.bind(this);
        this.onCommentChanged = this.onCommentChanged.bind(this);
        this.onRefresh = this.onRefresh.bind(this);
        this.getPlayer = this.getPlayer.bind(this);
        this.onRuntimeChanged = this.onRuntimeChanged.bind(this);
        this.errorHandler = this.errorHandler.bind(this);

        this.state = {
            isBuilding: false,
            isActivated: false,
            comment: null,
            players: [],
            error: null,
            hasUserSelectedPlayer: false,
            flowLibraryPlayer: null,
            isCheckingFlowLibrary: false,
            selectedRuntime: null,
            publishedFlow: null,
        };
    }

    componentDidMount() {
        this.onRefresh();
        this.props.fetchFlowLibraryItems();

        // Only administrators have read permissions for /api/admin/1/tenant/runtimes 
        if (this.props.currentUser.roleName === SYSTEM_ROLES.administrator.developerName) {
            this.props.getLocalRuntimesForTenant();
        }
    }

    onCommentChanged(e) {
        this.setState({ comment: e.target.value });
    }

    onRuntimeChanged(e) {
        const runtime = this.props.tenantRuntimeList.find(rt => rt.id === e.target.value);
        this.setState({
            selectedRuntime: runtime,
        });
    }

    onActivate() {
        this.setState({ isBuilding: true, isCheckingFlowLibrary: true, error: null });

        const comment = (manywho.utils.isNullOrWhitespace(this.state.comment)) ? null : `"${this.state.comment}"`;

        $.ajax({
            type: 'POST',
            url: `/api/draw/1/flow/snap/${this.props.flowId}`,
            contentType: 'application/json',
            data: comment,
            beforeSend: (xhr) => {
                xhr.setRequestHeader('Authorization', this.props.authenticationToken);
                xhr.setRequestHeader('ManyWhoTenant', this.props.tenantId);
            },
        }).then((response) => {

            this.setState({ publishedFlow: {
                id: {
                    id: this.props.flowId,
                    versionId: response.id.versionId,
                },
            } });
            // Deploy the snapshot to either the Local Runtime endpoint or the default runtime for the tenant.
            const activationUrl = this.state.selectedRuntime ?
                `/api/draw/1/flow/snap/${this.props.flowId}/${response.id.versionId}/deploy` :
                `/api/draw/1/flow/activation/${this.props.flowId}/${response.id.versionId}/true/true`;
            const activationPayload = this.state.selectedRuntime ?
                JSON.stringify({
                    runtimes: [
                        {
                            id: this.state.selectedRuntime.id,
                            isActive: true,
                            isDefault: true,
                        },
                    ],
                }) :
                null;

            return $.ajax({
                type: 'POST',
                url: activationUrl,
                contentType: 'application/json',
                data: activationPayload,
                beforeSend: (xhr) => {
                    xhr.setRequestHeader('Authorization', this.props.authenticationToken);
                    xhr.setRequestHeader('ManyWhoTenant', this.props.tenantId);
                },
            });
        }).fail((response) => {
            if (response) {
                this.setState({ error: response.statusText });
            }
        }).then(() => this.setState({ isBuilding: false, isActivated: true }));

        const isInFlowLibrary = this.props.items && this.props.items.find(flow => flow.flowId === this.props.flowId);
        const playerName = pathOr(null, ['preferredPlayer', 'name'], isInFlowLibrary);

        // This flow does have a preferredPlayer in Flow Library
        if (playerName) {
            // Check that this Player exists in their Tenant
            PlayerSource.get(playerName, this.props.tenantId).then(
                () => this.setState({
                    flowLibraryPlayer: playerName,
                    isCheckingFlowLibrary: false,
                }),
            ).catch(
                (error) => {
                    this.props.addNotification({
                        type: 'error',
                        message: error.message,
                        isPersistent: false,
                    });

                    this.setState({
                        flowLibraryPlayer: null,
                        isCheckingFlowLibrary: false,
                    });
                },
            );
            return;
        }

        this.setState({
            flowLibraryPlayer: null,
            isCheckingFlowLibrary: false,
        });
    }

    onRefresh() {
        PlayerSource.getAll(this.props.tenantId, this.props.authenticationToken)
            .then((players) => {
                this.setState({ players });
            });
    }

    getPlayer() {
        // Prefer the user selection over any other Player
        if (this.state.hasUserSelectedPlayer) {
            return this.props.selectedPlayer;
        }

        // Without user selection, we offer the Flow Library Player
        return this.state.flowLibraryPlayer ?
            this.state.flowLibraryPlayer :
            this.props.selectedPlayer;
    }

    errorHandler(error, info) {
        notify({
            error,
            params: {
                tenantId: this.props.tenantId,
                componentStack: info,                
            },
        });
    }

    render() {
        let body = null;
        let footer = null;
        const url = `${RUNTIME_URI}/${this.props.tenantId}/play/${this.getPlayer()}/?flow-id=${this.props.flowId}`;

        if (this.state.error) {
            body = (
                <ActivateFailed error={this.state.error} onActivate={this.onActivate} />
            );
        } else if (this.state.isBuilding || this.state.isCheckingFlowLibrary) {
            body = (
                React.createElement(manywho.component.getByName('wait'), { isVisible: true }, null)
            );
        } else if (this.state.isActivated) {
            footer = [
                <button className="btn btn-default" key="close" onClick={this.props.close}>Close</button>,
            ];
            if (this.state.selectedRuntime) {
                body = (
                    <ErrorBoundary FallbackComponent={ErrorFallback} onError={this.errorHandler}>
                        <ActivateSuccessRuntime
                            publishedFlow={this.state.publishedFlow}
                            tenantId={this.props.currentTenant.id}
                            selectedRuntime={this.state.selectedRuntime}
                        />
                    </ErrorBoundary>
                );
            } else {
                body = (
                    <ErrorBoundary FallbackComponent={ErrorFallback} onError={this.errorHandler}>
                        <ActivateSuccess
                            url={url}
                            players={this.state.players}
                            selectedPlayer={this.getPlayer()}
                            setSelectedPlayer={(e) => {
                                this.props.setSelectedPlayer(e);
                                this.setState({ hasUserSelectedPlayer: true });
                            }}
                            onRefresh={this.onRefresh}
                        />
                    </ErrorBoundary>
                );
                footer.unshift(<a className="btn btn-primary" key="run" href={url} target="_blank" rel="noopener noreferrer">Run</a>);
            }
        } else {
            body = (
                <ErrorBoundary FallbackComponent={ErrorFallback} onError={this.errorHandler}>
                    {
                        this.props.currentTenant && this.props.currentTenant.organization ?
                            (
                                <ActivateRuntime
                                    flowName={this.props.flows.find(flow => flow.id.id === this.props.flowId).developerName}
                                    tenant={this.props.currentTenant}
                                    runtimes={this.props.tenantRuntimeList}
                                    onChange={this.onRuntimeChanged}
                                />
                            ) :
                            null
                    }
                    <ActivatePublish comment={this.state.comment} onCommentChanged={this.onCommentChanged} />
                </ErrorBoundary>
            );
            footer = <Button bsStyle="success" onClick={this.onActivate}>Publish</Button>;
        }

        const bodyClassName =
            this.state.error ?
                'text-center' :
                this.state.isActivated ?
                    'activation-successful' :
                    '';

        return (
            <Modal show onHide={this.props.close} container={this.props.container} ref="modal">
                <Modal.Header closeButton>
                    <Modal.Title>Publish Flow</Modal.Title>
                </Modal.Header>
                <Modal.Body className={bodyClassName}>
                    {body}
                </Modal.Body>
                <Modal.Footer>
                    {footer}
                </Modal.Footer>
            </Modal>
        );

    }

}

const mapStateToProps = ({
    flowLibrary: { items },
    currentTenant,
    flows: { all },
    localRuntimes: { tenantRuntimeList },
    currentUser,
}) => ({
    items,
    currentTenant,
    flows: all,
    tenantRuntimeList,
    currentUser,
});

const mapDispatchToProps = {
    fetchFlowLibraryItems,
    addNotification,
    getLocalRuntimesForTenant,
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(Activate);
